/**
 * useAdminLogs Hook
 * 작업 로그 조회 및 분석 커스텀 훅
 */

import { useState, useCallback } from 'react';
import { AdminLogRepository } from '../repositories/AdminLogRepository';
import { AdminLogService } from '../services/AdminLogService';
import {
  AdminLog,
  AdminAction,
  EntityType,
  AdminLogStats,
  AdminLogQueryParams,
} from '../models';

const adminLogRepository = new AdminLogRepository();
const adminLogService = new AdminLogService(adminLogRepository);

export const useAdminLogs = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [logStats, setLogStats] = useState<AdminLogStats | null>(null);

  /**
   * 로그 조회 (필터링)
   */
  const fetchLogs = useCallback(async (params?: AdminLogQueryParams) => {
    setLoading(true);
    setError(null);

    try {
      const data = await adminLogService.getLogs(params);
      setLogs(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 내 작업 로그 조회
   */
  const fetchMyLogs = useCallback(async (limit?: number) => {
    setLoading(true);
    setError(null);

    try {
      const data = await adminLogService.getMyLogs(limit);
      setLogs(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 특정 엔티티의 로그 조회
   */
  const fetchLogsByEntity = useCallback(
    async (entityType: EntityType, entityId: string) => {
      setLoading(true);
      setError(null);

      try {
        const data = await adminLogService.getLogsByEntity(
          entityType,
          entityId
        );
        setLogs(data);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : '조회에 실패했습니다.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * 로그 통계 조회
   */
  const fetchStats = useCallback(async (days?: number) => {
    setLoading(true);
    setError(null);

    try {
      const data = await adminLogService.getLogStats(days);
      setLogStats(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '통계 조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 오래된 로그 삭제
   */
  const cleanupLogs = useCallback(async (days: number): Promise<number> => {
    setLoading(true);
    setError(null);

    try {
      const deletedCount = await adminLogService.cleanupOldLogs(days);
      return deletedCount;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '삭제에 실패했습니다.';
      setError(errorMessage);
      return 0;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 로그 필터링
   */
  const filterLogs = useCallback(
    (filter: {
      action?: AdminAction;
      entityType?: EntityType;
      adminId?: string;
      startDate?: string;
      endDate?: string;
    }) => {
      const filtered = adminLogService.filterLogs(logs, filter);
      setLogs(filtered);
    },
    [logs]
  );

  /**
   * 로그 검색
   */
  const search = useCallback(
    (query: string) => {
      const filtered = adminLogService.searchLogs(logs, query);
      setLogs(filtered);
    },
    [logs]
  );

  /**
   * 액션별 통계
   */
  const getActionDistribution = useCallback(() => {
    return adminLogService.getActionDistribution(logs);
  }, [logs]);

  /**
   * 엔티티별 통계
   */
  const getEntityDistribution = useCallback(() => {
    return adminLogService.getEntityDistribution(logs);
  }, [logs]);

  /**
   * 관리자별 작업 건수
   */
  const getAdminActivityCount = useCallback(() => {
    return adminLogService.getAdminActivityCount(logs);
  }, [logs]);

  /**
   * 일별 작업 건수
   */
  const getDailyActivityCount = useCallback(() => {
    return adminLogService.getDailyActivityCount(logs);
  }, [logs]);

  /**
   * 시간대별 작업 건수
   */
  const getHourlyActivityCount = useCallback(() => {
    return adminLogService.getHourlyActivityCount(logs);
  }, [logs]);

  /**
   * 최근 N일간의 활동 추이
   */
  const getRecentActivityTrend = useCallback(
    (days: number) => {
      return adminLogService.getRecentActivityTrend(logs, days);
    },
    [logs]
  );

  /**
   * 감사 추적용 상세 로그 생성
   */
  const generateAuditTrail = useCallback(() => {
    return adminLogService.generateAuditTrail(logs);
  }, [logs]);

  /**
   * CSV 내보내기용 데이터 생성
   */
  const generateCSVData = useCallback(() => {
    return adminLogService.generateCSVData(logs);
  }, [logs]);

  return {
    logs,
    logStats,
    loading,
    error,
    fetchLogs,
    fetchMyLogs,
    fetchLogsByEntity,
    fetchStats,
    cleanupLogs,
    filterLogs,
    search,
    getActionDistribution,
    getEntityDistribution,
    getAdminActivityCount,
    getDailyActivityCount,
    getHourlyActivityCount,
    getRecentActivityTrend,
    generateAuditTrail,
    generateCSVData,
  };
};
